import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type {
  NotificationSchedule as Schedule,
  NotificationSendProblem,
  WriteRejection,
} from "@estelle/domain";
import { useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";

const SCHEDULE_KEY = ["notifications", "schedule"] as const;
const PROBLEMS_KEY = ["notifications", "schedule", "problems"] as const;

/** The zones the team is likely to need; the one in force is added if it is not here. */
const ZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Europe/London",
  "America/New_York",
  "UTC",
];

/**
 * When the ERP speaks up on its own (E6, #446, slice 2): the time the morning
 * summary is created, and one set of quiet hours for everyone.
 *
 * Administrators only — the section needs `settings.company.manage`, and the
 * API refuses anyone else on every route behind it. There are no per-person
 * quiet hours. Quiet hours hold email (and push, when it arrives); the bell is
 * never held back, so a reminder due at night still waits there, and an email
 * held overnight goes out when the quiet hours end.
 */
function ScheduleForm({ saved }: { saved: Schedule }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const summaryId = useId();
  const zoneId = useId();
  const quietId = useId();
  const startId = useId();
  const endId = useId();

  const [summaryTime, setSummaryTime] = useState(saved.morningSummaryTime);
  const [timeZone, setTimeZone] = useState(saved.timeZone);
  const [quietOn, setQuietOn] = useState(saved.quietHours.enabled);
  const [start, setStart] = useState(saved.quietHours.start);
  const [end, setEnd] = useState(saved.quietHours.end);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [done, setDone] = useState(false);

  const zones = ZONES.includes(saved.timeZone)
    ? ZONES
    : [saved.timeZone, ...ZONES];

  const save = useMutation({
    mutationFn: () =>
      data.notifications.saveSchedule({
        morningSummaryTime: summaryTime,
        timeZone,
        quietHours: { enabled: quietOn, start, end },
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        setDone(true);
        queryClient.setQueryData(SCHEDULE_KEY, result.data);
      } else {
        setDone(false);
        setRejection(result.rejection);
      }
    },
  });

  const changed =
    summaryTime !== saved.morningSummaryTime ||
    timeZone !== saved.timeZone ||
    quietOn !== saved.quietHours.enabled ||
    start !== saved.quietHours.start ||
    end !== saved.quietHours.end;

  const touch = () => {
    setDone(false);
  };

  const fieldError = (name: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[name] : undefined;

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Reminders and quiet hours</CardTitle>
        <CardDescription>
          One setting for everyone. The morning summary lists each person&apos;s
          tasks due today and overdue, once a day, in the bell; nothing is
          created for someone with nothing to list. The daily email digest of
          everything unread goes out at the same time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={summaryId} className="text-sm font-medium">
              Morning summary at
            </Label>
            <Input
              id={summaryId}
              type="time"
              value={summaryTime}
              disabled={save.isPending}
              aria-invalid={fieldError("morningSummaryTime") ? true : undefined}
              onChange={(event) => {
                setSummaryTime(event.target.value);
                touch();
              }}
            />
            {fieldError("morningSummaryTime") ? (
              <p className="text-destructive text-sm">
                {fieldError("morningSummaryTime")}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={zoneId} className="text-sm font-medium">
              Time zone
            </Label>
            <Select
              value={timeZone}
              disabled={save.isPending}
              onValueChange={(value) => {
                setTimeZone(value);
                touch();
              }}
            >
              <SelectTrigger id={zoneId} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {zones.map((zone) => (
                  <SelectItem key={zone} value={zone}>
                    {zone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <fieldset className="space-y-3 rounded-md border p-4">
          <legend className="px-1 text-sm font-medium">Quiet hours</legend>
          <div className="flex items-start gap-2">
            <Checkbox
              id={quietId}
              checked={quietOn}
              disabled={save.isPending}
              onCheckedChange={(value) => {
                setQuietOn(value === true);
                touch();
              }}
            />
            <Label htmlFor={quietId} className="text-sm leading-snug">
              Hold phone and email notifications during these hours
            </Label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor={startId} className="text-sm">
                From
              </Label>
              <Input
                id={startId}
                type="time"
                value={start}
                disabled={save.isPending || !quietOn}
                aria-invalid={fieldError("quietHours.start") ? true : undefined}
                onChange={(event) => {
                  setStart(event.target.value);
                  touch();
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={endId} className="text-sm">
                Until
              </Label>
              <Input
                id={endId}
                type="time"
                value={end}
                disabled={save.isPending || !quietOn}
                aria-invalid={fieldError("quietHours.end") ? true : undefined}
                onChange={(event) => {
                  setEnd(event.target.value);
                  touch();
                }}
              />
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            An end earlier than the start runs past midnight. The bell is never
            held back: a reminder due at night is still waiting there in the
            morning.
          </p>
        </fieldset>

        {rejection ? (
          <RejectionNotice
            rejection={rejection}
            onReload={() => {
              setRejection(null);
            }}
          />
        ) : null}
        {done ? (
          <p
            role="status"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            Saved.
          </p>
        ) : null}

        <Button
          type="button"
          disabled={!changed || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
      </CardContent>
    </Card>
  );
}

const WHAT: Record<NotificationSendProblem["kind"], string> = {
  task_reminder: "Task reminder",
  morning_summary: "Morning summary",
  email: "Email",
  email_digest: "Daily digest email",
};

/**
 * Reminders, summaries and emails that could not be created or sent. A failure
 * is a row you can read here, not a line in a server log; the task is named by
 * its reference, never its title.
 */
function Problems() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: PROBLEMS_KEY,
    queryFn: () => data.notifications.listSendProblems(),
  });

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Not delivered</CardTitle>
        <CardDescription>
          Reminders, summaries and emails that were skipped or failed, newest
          first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <TableSkeleton rows={2} />
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : query.data.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing has gone missing.
          </p>
        ) : (
          <ul className="divide-y">
            {query.data.map((problem) => (
              <li key={problem.id} className="space-y-0.5 py-3 text-sm">
                <p className="font-medium">
                  {WHAT[problem.kind]}
                  {problem.reference ? ` · ${problem.reference}` : ""} for{" "}
                  {problem.recipientName}
                </p>
                <p className="text-muted-foreground">
                  {problem.outcome === "failed" ? "Failed" : "Skipped"}:{" "}
                  {problem.reason}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function NotificationSchedule() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: SCHEDULE_KEY,
    queryFn: () => data.notifications.getSchedule(),
  });

  if (query.isPending) return <TableSkeleton rows={3} />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  return (
    <div className="space-y-6">
      <ScheduleForm saved={query.data} />
      <Problems />
    </div>
  );
}
